import React, { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiService } from '../services/apiService'
import {
  HomeIcon,
  TruckIcon,
  UsersIcon,
  DocumentTextIcon,
  BellIcon,
  CogIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  CheckCircleIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  CpuChipIcon,
  BuildingOfficeIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  TagIcon,
  FolderIcon,
  DocumentIcon,
  XCircleIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  CubeIcon,
  RocketLaunchIcon,
  FunnelIcon,
  MegaphoneIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  BoltIcon,
  CalendarDaysIcon,
  PresentationChartBarIcon,
  SparklesIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'

const Sidebar = ({ collapsed, open, onToggle, onClose }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [badges, setBadges] = useState({
    vehicles: 0,
    clients: 0,
    contracts: 0,
    maintenance: 0
  })
  const [expandedSections, setExpandedSections] = useState({
    contratos: false,      // Contratos retraído por padrão
    frota: false,          // Frota retraído por padrão
    rastreamento: false,   // Rastreamento retraído por padrão
    manutencao: false,     // Manutenção retraído por padrão
    fluxoCaixa: false,     // Fluxo de Caixa retraído por padrão
    cobrancas: false,      // Cobranças retraído por padrão
    usuarios: false,       // Usuários retraído por padrão
    cadastros: false,      // Cadastros retraído por padrão
    sistema: false,        // Sistema retraído por padrão
    marketing: false       // Marketing retraído por padrão
  })

  // Buscar badges ao montar e a cada 5 minutos
  useEffect(() => {
    loadBadges()
    const interval = setInterval(loadBadges, 5 * 60 * 1000) // 5 minutos
    return () => clearInterval(interval)
  }, [])

  const loadBadges = async () => {
    try {
      const data = await apiService.getDashboardData()
      setBadges({
        vehicles: data.kpis.total_vehicles || 0,
        clients: data.kpis.active_clients || 0,
        contracts: data.kpis.active_contracts || 0,
        maintenance: data.kpis.maintenance_vehicles || 0
      })
    } catch (error) {
      console.error('Erro ao carregar badges:', error)
    }
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const navigation = [
    // 1. VISÃO GERAL
    {
      title: 'Visão Geral',
      items: [
        { name: 'Dashboard BI', href: '/dashboard', icon: ChartBarIcon, badge: 'BI', badgeType: 'ai' },
        { name: 'Predições IA', href: '/predictions', icon: CpuChipIcon, badge: 'IA', badgeType: 'ai' }
      ]
    },
    // 2. VENDAS & CONTRATOS
    {
      title: 'Vendas & Contratos',
      items: [
        { name: 'Clientes', href: '/clients', icon: UsersIcon, badge: badges.clients },
        {
          name: 'Contratos',
          icon: DocumentTextIcon,
          expandable: true,
          expanded: expandedSections.contratos,
          onToggle: () => toggleSection('contratos'),
          subitems: [
            { name: 'Lista de Contratos', href: '/contracts', icon: DocumentTextIcon, badge: badges.contracts },
            { name: 'Cotação Automática', href: '/quotes', icon: CreditCardIcon, badge: 'IA', badgeType: 'ai' },
            { name: 'Biblioteca de Cláusulas', href: '/contracts/clauses', icon: FolderIcon },
            { name: 'Configurações de Contrato', href: '/contract-settings', icon: CogIcon, badge: 'NOVO', badgeType: 'success' }
          ]
        },
        { name: 'Marketplace', href: '/marketplace', icon: BuildingOfficeIcon, badge: 'BETA', badgeType: 'beta' }
      ]
    },
    // 3. FINANCEIRO
    {
      title: 'Financeiro',
      items: [
        { name: 'Visão Geral', href: '/cash-flow', icon: ChartBarIcon },
        {
          name: 'Fluxo de Caixa',
          icon: BanknotesIcon,
          expandable: true,
          expanded: expandedSections.fluxoCaixa,
          onToggle: () => toggleSection('fluxoCaixa'),
          subitems: [
            { name: 'Transações', href: '/cash-flow/transactions', icon: CreditCardIcon },
            { name: 'Transferências', href: '/cash-flow/transfers', icon: ArrowsRightLeftIcon },
            { name: 'Contas Bancárias', href: '/cash-flow/banks', icon: BuildingLibraryIcon }
          ]
        },
        {
          name: 'Cobranças',
          icon: DocumentTextIcon,
          expandable: true,
          expanded: expandedSections.cobrancas,
          onToggle: () => toggleSection('cobrancas'),
          subitems: [
            { name: 'Faturas', href: '/invoices', icon: DocumentTextIcon },
            { name: 'A Receber', href: '/receivables', icon: CreditCardIcon },
            { name: 'A Pagar', href: '/payables', icon: CreditCardIcon }
          ]
        }
      ]
    },
    // 4. OPERAÇÕES
    {
      title: 'Operações',
      items: [
        {
          name: 'Frota',
          icon: TruckIcon,
          expandable: true,
          expanded: expandedSections.frota,
          onToggle: () => toggleSection('frota'),
          subitems: [
            { name: 'Veículos', href: '/vehicles', icon: TruckIcon, badge: badges.vehicles },
            { name: 'Equipamentos Especiais', href: '/equipamentos-especiais', icon: CubeIcon },
            { name: 'Categorias de Equipamentos', href: '/equipment-categories', icon: CubeIcon }
          ]
        },
        {
          name: 'Rastreamento',
          icon: MapPinIcon,
          expandable: true,
          expanded: expandedSections.rastreamento,
          onToggle: () => toggleSection('rastreamento'),
          subitems: [
            { name: 'Mapa GPS', href: '/tracking', icon: MapPinIcon },
            { name: 'Dashboard da Frota', href: '/fleet-dashboard', icon: ChartBarIcon },
            { name: 'Relatórios GPS', href: '/gps-reports', icon: DocumentArrowDownIcon },
            { name: 'Diagnóstico OBD', href: '/obd', icon: CpuChipIcon },
            { name: 'Histórico', href: '/tracking', icon: MapPinIcon }
          ]
        },
        {
          name: 'Manutenção',
          icon: WrenchScrewdriverIcon,
          expandable: true,
          expanded: expandedSections.manutencao,
          onToggle: () => toggleSection('manutencao'),
          subitems: [
            { name: 'Preditiva (IA)', href: '/predictive-maintenance', icon: WrenchScrewdriverIcon, badge: 'IA', badgeType: 'ai' },
            { name: 'Inspeções Unificadas', href: '/inspections', icon: CheckCircleIcon, badge: 'NOVO', badgeType: 'success' },
            { name: 'Templates de Vistoria', href: '/settings/inspection-templates', icon: CogIcon },
            { name: 'Gestão de Pneus', href: '/tires', icon: CheckCircleIcon },
            { name: 'Configurações', href: '/maintenance-config', icon: CogIcon, badge: 'NOVO', badgeType: 'success' }
          ]
        },
        { name: 'Combustível', href: '/fuel', icon: CreditCardIcon },
        { name: 'Compliance', href: '/compliance', icon: DocumentTextIcon }
      ]
    },
    // 5. ADMINISTRAÇÃO
    {
      title: 'Administração',
      items: [
        {
          name: 'Usuários',
          icon: UsersIcon,
          expandable: true,
          expanded: expandedSections.usuarios,
          onToggle: () => toggleSection('usuarios'),
          subitems: [
            { name: 'Gestão de Usuários', href: '/users-management', icon: UsersIcon, badge: 'NOVO', badgeType: 'success' },
            { name: 'Perfis e Permissões', href: '/user-roles', icon: ShieldCheckIcon, badge: 'NOVO', badgeType: 'success' },
            { name: 'Motoristas', href: '/drivers', icon: UserGroupIcon }
          ]
        },
        {
          name: 'Cadastros',
          icon: FolderIcon,
          expandable: true,
          expanded: expandedSections.cadastros,
          onToggle: () => toggleSection('cadastros'),
          subitems: [
            { name: 'Fornecedores', href: '/cadastros/fornecedores', icon: UserGroupIcon },
            { name: 'Formas de Pagamento', href: '/cadastros/formas-pagamento', icon: CreditCardIcon },
            { name: 'Tipos de Manutenção', href: '/cadastros/tipos-manutencao', icon: WrenchScrewdriverIcon },
            { name: 'Tipos de Veículo', href: '/cadastros/tipos-veiculo', icon: TruckIcon },
            { name: 'Tipos de Documento', href: '/cadastros/tipos-documento', icon: DocumentIcon },
            { name: 'Motivos de Cancelamento', href: '/cadastros/motivos-cancelamento', icon: XCircleIcon },
            { name: 'Contas Bancárias', href: '/cadastros/contas-bancarias', icon: BuildingLibraryIcon },
            { name: 'Categorias Financeiras', href: '/cash-flow/categories', icon: TagIcon }
          ]
        },
        {
          name: 'Sistema',
          icon: CogIcon,
          expandable: true,
          expanded: expandedSections.sistema,
          onToggle: () => toggleSection('sistema'),
          subitems: [
            { name: 'Configurações Gerais', href: '/configuracoes', icon: CogIcon },
            { name: 'Integrações', href: '/integrations', icon: CpuChipIcon, badge: 'WhatsApp', badgeType: 'success' },
            { name: 'Política de Crédito', href: '/credit-policy', icon: ShieldCheckIcon, badge: 'NOVO', badgeType: 'info' },
            { name: 'Notificações', href: '/notifications', icon: BellIcon }
          ]
        }
      ]
    },
    // 6. RELATÓRIOS
    {
      title: 'Relatórios',
      items: [
        { name: 'Gerenciais', href: '/reports', icon: DocumentDuplicateIcon, badge: 'FUNCIONAL', badgeType: 'success' },
        { name: 'Email Analytics', href: '/email-analytics', icon: ChartBarIcon, badge: 'NOVO', badgeType: 'success' },
        { name: 'Alertas (IA)', href: '/intelligent-alerts', icon: BellIcon, badge: 'IA', badgeType: 'ai' }
      ]
    },
    // 7. MARKETING & CRM
    {
      title: 'Marketing & CRM',
      items: [
        { name: 'Dashboard MKT', href: '/marketing', icon: PresentationChartBarIcon, badge: 'NOVO', badgeType: 'success' },
        {
          name: 'CRM B2B',
          icon: RocketLaunchIcon,
          expandable: true,
          expanded: expandedSections.marketing,
          onToggle: () => toggleSection('marketing'),
          subitems: [
            { name: 'Leads', href: '/marketing/leads', icon: UsersIcon },
            { name: 'Pipeline', href: '/marketing/pipeline', icon: FunnelIcon },
            { name: 'Conteúdo', href: '/marketing/content', icon: DocumentTextIcon },
            { name: 'WhatsApp', href: '/marketing/whatsapp', icon: ChatBubbleLeftRightIcon },
            { name: 'Campanhas', href: '/marketing/campaigns', icon: EnvelopeIcon },
            { name: 'Automação', href: '/marketing/automation', icon: BoltIcon },
            { name: 'Calendário', href: '/marketing/calendar', icon: CalendarDaysIcon },
            { name: 'IA Estrategista', href: '/marketing/ia', icon: SparklesIcon, badge: 'IA', badgeType: 'ai' },
            { name: 'Integrações', href: '/marketing/integrations', icon: CpuChipIcon },
            { name: 'Analytics IA', href: '/marketing/analytics', icon: ChartBarIcon, badge: 'NOVO', badgeType: 'success' }
          ]
        }
      ]
    }
  ]

  const handleLogout = () => {
    logout()
    onClose?.()
  }

  const renderNavItem = (item, isSubitem = false) => {
    if (item.expandable) {
      return (
        <div key={item.name}>
          <button
            onClick={item.onToggle}
            className={`
              w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
              text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700
              ${collapsed ? 'justify-center' : 'space-x-3'}
            `}
            title={collapsed ? item.name : ''}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{item.name}</span>
                {item.expanded ? 
                  <ChevronDownIcon className="w-4 h-4" /> : 
                  <ChevronRightIcon className="w-4 h-4" />
                }
              </>
            )}
          </button>
          
          {!collapsed && item.expanded && item.subitems && (
            <div className="ml-4 mt-1 space-y-1">
              {item.subitems.map((subitem) => renderNavItem(subitem, true))}
            </div>
          )}
        </div>
      )
    }

    return (
      <NavLink
        key={item.href}
        to={item.href}
        className={({ isActive }) => `
          flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
          ${isActive
            ? 'bg-primary-50 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border-r-2 border-secondary-500 shadow-viploc'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }
          ${collapsed ? 'justify-center' : 'space-x-3'}
          ${isSubitem ? 'ml-2 text-xs' : ''}
        `}
        title={collapsed ? item.name : ''}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1">{item.name}</span>
            {item.badge && (
              <span className={`
                px-2 py-0.5 text-xs rounded-full font-medium
                ${item.badgeType === 'new' ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                  item.badgeType === 'ai' ? 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900 dark:text-secondary-300' :
                  item.badgeType === 'beta' ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                  item.badgeType === 'soon' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' :
                  item.badgeType === 'success' ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                  'bg-primary-100 text-primary-800 dark:bg-primary-700 dark:text-primary-300'
                }
              `}>
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    )
  }

  return (
    <>
      {/* Sidebar Desktop */}
      <div className={`
        fixed left-0 top-0 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
        transition-all duration-300 ease-in-out z-50
        ${collapsed ? 'w-20' : 'w-72'}
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-orange">
                <TruckIcon className="w-5 h-5 text-white" />
              </div>
              {!collapsed && (
                <div>
                  <h1 className="text-xl font-bold font-display bg-gradient-primary bg-clip-text text-transparent">VIPLOC</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sistema de Gestão</p>
                </div>
              )}
            </div>
            
            {/* Toggle Button */}
            <button
              onClick={onToggle}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                collapsed ? 'hidden lg:block' : ''
              }`}
            >
              <Bars3Icon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
            {navigation.map((section) => (
              <div key={section.title}>
                {!collapsed && (
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {section.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => renderNavItem(item))}
                </div>
              </div>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className={`
                w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium
                text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/50
                transition-colors duration-200
                ${collapsed ? 'justify-center' : 'space-x-3'}
              `}
              title={collapsed ? 'Sair do Sistema' : ''}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {!collapsed && <span>Sair do Sistema</span>}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar